// ============================================================
// LitigationFriendCard.tsx — B4 follow-ups: who acts for a CHILD or PROTECTED
// PARTY, on the case's Client details (it matters for the whole case). Shown
// only for them (ClientDetailsSection decides). Self-loading:
//   • the LITIGATION FRIEND — name, relationship, address, email, phone.
//     Bills, credit notes and the settlement statement go to them;
//   • their consent to receive those BY EMAIL at one exact address (the
//     client's rules: a changed email needs consent again) — record / remove;
//   • the DEPUTY / LPA ATTORNEY (or other) — type, name, reference, order
//     date. A deputy or LPA attorney covers the £100,000 Court of Protection check.
// Backend: controllers/litigationFriendController (validated, audited).
//
// (Batch 2) READ-FIRST, like the Claim card: a read-only view with a pencil;
// the pencil opens a DRAFT (Save / Cancel only while editing; the unsaved-
// changes guard while it differs). (B4 follow-ups) The address is the
// client's address block — postcode search + line 1, line 2, town, county,
// postcode (clientInfoRegistry AddressFieldsEdit) — plus "Same address as the
// client" (copies the client's fields in — nothing linked is stored). An old
// one-box address the server couldn't split is shown as typed, with a note
// asking for it to be re-entered; it's never wiped by saving another field
// (the address fields are only sent when they changed). Loading + Retry; a 400's
// `field` / `fields` show under the right inputs; consent errors show even
// with the form closed. Every save announces 'litigationFriend'
// (utils/caseChangeEvents) so the Settlement page + statement panel re-read.
// Root id="litigation-friend": the place param focus=litigation-friend
// (Settlement's "Edit in Client details") scrolls here and focuses the pencil.
// (E-sign fixes B) The codes mobile follows the agreed design (utils/esignMobile,
// as the client card): a UK-mobile Phone is labelled "(used for e-signature
// codes)" and the separate field is hidden (Save sends litigationFriendMobile:
// null — the server uses the phone); the separate "Mobile for e-signature
// codes" shows when a different mobile is stored, the phone isn't a UK mobile
// ("This looks like a landline — codes need a mobile"), or "Use a different
// mobile for codes" is pressed.
// ============================================================
import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { getLitigationFriendApi, getLitigationFriendMobileChangesApi, putLitigationFriendApi, recordLitigationFriendConsentApi, removeLitigationFriendConsentApi } from '../../api/casevoApi'
import { REPRESENTATIVE_TYPE_LABELS, type LitigationFriendState, type Representatives, type RepresentativeType } from '../../types/settlementStatement.types'
import type { Address } from '../../types/case.types'
import { BIcon, B_PRIMARY, B_SECONDARY_SM, CARD, CardHead, INPUT, NOTE, errorText, fmtDate } from '../billing/billingUi'
import FormField from '../shared/FormField'
import FieldError from '../shared/FieldError'
import DateInput from '../shared/DateInput'
import ActionBox from '../shared/ActionBox'
import ConsentEditCard from '../billing/ConsentEditCard'
import { AddressFieldsEdit, EMPTY_ADDRESS, IcEdit, type AddressValue } from './clientInfoRegistry'
import { CARD_EDITING } from './ClientInfoSections'
import { UnsavedGuard } from '../../hooks/useUnsavedChangesGuard'
import { initialPlaceParam, usePlaceParam } from '../../navigation/navigate'
import { emitCaseChange, useCaseChange, type CaseChangeKind } from '../../utils/caseChangeEvents'
import { useLiveEditNotice } from '../../hooks/useLiveEditNotice'
import LiveUpdateNote from '../shared/LiveUpdateNote'
import { EMAIL_FORMAT_ERROR, emailFormatError, isValidEmail, phoneFormatError } from '../../utils/contactFormat'
import { consentChangedMessage } from '../../utils/noEmail'
import { londonToday, textDateToIso } from '../../utils/civilDate'
import { CONSENT_METHOD_OPTIONS } from './BillEmailConsentCard'
import { useEsignPilot } from '../../hooks/useEsignPilot'
import { formatUkMobile } from '../../utils/mobileNumber'
import { MOBILE_HINT, MOBILE_LABEL, MobileChangedHint, MobileInput, mobileE164Of, mobileInputError } from './MobileNumberField'
import { CODES_LABEL_SUFFIX, LANDLINE_NOTE, codesMobileView, normaliseUkMobile } from '../../utils/esignMobile'

// Under 18 on `today` (the DOB as YYYY-MM-DD).
export const isChildToday = (dobIso: string | null, today: string = londonToday()): boolean => {
  if (!dobIso || !/^\d{4}-\d{2}-\d{2}$/.test(dobIso)) return false
  const eighteenth = `${Number(dobIso.slice(0, 4)) + 18}${dobIso.slice(4)}`
  return dobIso <= today && today < eighteenth
}
// The DOB text (DD/MM/YYYY or ISO) → ISO, or null.
export const dobIsoOf = (raw: string | null | undefined): string | null => (raw ? (textDateToIso(raw) ?? null) : null)
// A 5-field address as the litigation friend's one address text (a line each, blanks dropped).
export const addressText = (a: Partial<Address> | null | undefined): string =>
  a ? [a.line1, a.line2, a.town, a.county, a.postcode].map((x) => (x ?? '').trim()).filter(Boolean).join('\n') : ''

// The place param that brings the user here (Settlement → "Edit in Client details").
export const LF_FOCUS = 'litigation-friend'
export const LF_ROOT_ID = 'litigation-friend'
export const LF_EDIT_ID = 'litigation-friend-edit'

// (B4 follow-ups) The address in the client's separate fields.
const ADDR_KEYS = {
  line1: 'litigationFriendAddressLine1', line2: 'litigationFriendAddressLine2', town: 'litigationFriendAddressTown',
  county: 'litigationFriendAddressCounty', postcode: 'litigationFriendAddressPostcode',
} as const
const ADDR_FIELD_KEYS = Object.values(ADDR_KEYS) as string[]
export const lfAddressOf = (r: Partial<Representatives> | null | undefined): AddressValue => ({
  line1: r?.litigationFriendAddressLine1 ?? '', line2: r?.litigationFriendAddressLine2 ?? '', town: r?.litigationFriendAddressTown ?? '',
  county: r?.litigationFriendAddressCounty ?? '', postcode: r?.litigationFriendAddressPostcode ?? '',
})
const addressFields = (a: AddressValue): Pick<Representatives, typeof ADDR_KEYS[keyof typeof ADDR_KEYS]> => ({
  litigationFriendAddressLine1: a.line1, litigationFriendAddressLine2: a.line2, litigationFriendAddressTown: a.town,
  litigationFriendAddressCounty: a.county, litigationFriendAddressPostcode: a.postcode,
})
const pick = (s: LitigationFriendState | Representatives): Representatives => ({
  litigationFriendName: s.litigationFriendName, litigationFriendRelationship: s.litigationFriendRelationship, litigationFriendAddress: s.litigationFriendAddress,
  ...addressFields(lfAddressOf(s)),
  litigationFriendEmail: s.litigationFriendEmail, litigationFriendPhone: s.litigationFriendPhone,
  // B4c (D4) Shown / edited as typed ("07700 900123"); the server stores E.164.
  litigationFriendMobile: formatUkMobile(s.litigationFriendMobile ?? ''),
  representativeType: s.representativeType,
  representativeName: s.representativeName, representativeReference: s.representativeReference, representativeOrderDate: s.representativeOrderDate,
})
const norm = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase()
const anyAddress = (a: AddressValue | null | undefined): boolean => !!a && Object.values(a).some((v) => v.trim())
// The same address (case, spacing and the postcode's space ignored).
export const sameAddress = (a: AddressValue | null | undefined, b: AddressValue | null | undefined): boolean => {
  if (!a || !b) return false
  const k = (x: AddressValue): string => [x.line1, x.line2, x.town, x.county, x.postcode.replace(/\s+/g, '')].map((v) => norm(v).replace(/\s+/g, ' ')).join('|')
  return k(a) === k(b)
}
// The printed / shown lines in the client's order (line 1, line 2, town, county); the postcode is its own row.
const addressLinesOf = (a: AddressValue): string[] => [a.line1, a.line2, a.town, a.county].map((x) => x.trim()).filter(Boolean)
export const LEGACY_ADDRESS_NOTE = 'Couldn’t split this address automatically — please re-enter it in the fields.'
const methodLabel = (m: string | null): string => CONSENT_METHOD_OPTIONS.find((o) => o.value === m)?.label ?? 'Not stated'
// A 400's `field` and/or `fields: { name: message }` → one message per field.
type ServerErr = { response?: { data?: { field?: string; fields?: Record<string, string>; error?: string } } }
export const serverFieldErrors = (e: unknown): Record<string, string> => {
  const d = (e as ServerErr)?.response?.data
  const out: Record<string, string> = {}
  if (d?.fields && typeof d.fields === 'object') for (const [k, v] of Object.entries(d.fields)) if (typeof v === 'string') out[k] = v
  if (d?.field && !out[d.field]) out[d.field] = d.error ?? 'Check this field.'
  return out
}
const PENCIL = 'w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-brand transition-[background-color,color] focus:outline-none casevo-focus'

// (D12) The data this card shows.
const LF_LIVE_KINDS: CaseChangeKind[] = ['litigationFriend']

const LitigationFriendCard: React.FC<{
  caseId: string
  childOrProtected: 'CHILD' | 'PROTECTED_PARTY'
  // The client's address (its five fields) — for "Same address as the client".
  clientAddress?: AddressValue | null
}> = ({ caseId, childOrProtected, clientAddress = null }) => {
  const clientHas = anyAddress(clientAddress)
  const id = useId()
  // B4c (D4) The mobile for e-signature codes shows only with the firm's pilot on.
  const pilot = useEsignPilot()
  const [state, setState] = useState<LitigationFriendState | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  // The draft (null = the read-only view).
  const [draft, setDraft] = useState<Representatives | null>(null)
  const [sameAsClient, setSameAsClient] = useState(false)
  const [busy, setBusy] = useState(false)
  const [serverErr, setServerErr] = useState<Record<string, string>>({})
  const [saveErr, setSaveErr] = useState<string | null>(null)
  const [tried, setTried] = useState(false)
  const [orderTyping, setOrderTyping] = useState(false)
  // (E-sign fixes B) "Use a different mobile for codes" pressed while the phone is a UK mobile.
  const [lfSeparate, setLfSeparate] = useState(false)
  // The consent form.
  const [consentOpen, setConsentOpen] = useState(false)
  const [cEmail, setCEmail] = useState('')
  const [cDate, setCDate] = useState<string | null>(londonToday())
  const [cMethod, setCMethod] = useState('')
  const [cErr, setCErr] = useState<{ field?: string; text: string } | null>(null)
  // Remove-consent (and other view-mode) errors — shown with the form closed.
  const [viewErr, setViewErr] = useState<string | null>(null)

  const load = useCallback((): (() => void) => {
    let live = true
    setLoading(true); setLoadErr(null)
    getLitigationFriendApi(caseId)
      .then((s) => { if (live) setState(s) })
      .catch((e) => { if (live) setLoadErr(errorText(e, 'Couldn’t load the litigation friend.')) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [caseId])
  useEffect(() => load(), [load])

  // focus=litigation-friend → scroll here and focus the pencil (or Retry) once loaded.
  const rootRef = useRef<HTMLElement>(null)
  const [focusReq, setFocusReq] = useState<string | null>(() => initialPlaceParam('focus'))
  usePlaceParam('focus', focusReq, setFocusReq)
  useEffect(() => {
    if (focusReq !== LF_FOCUS || loading) return
    const root = rootRef.current
    root?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
    const target = root?.querySelector<HTMLElement>(`#${LF_EDIT_ID}, [data-lf-retry]`)
    target?.focus()
    setFocusReq(null)
  }, [focusReq, loading])

  const editing = draft !== null
  const f = draft ?? (state ? pick(state) : null)
  const dirty = !!state && !!draft && JSON.stringify(draft) !== JSON.stringify(pick(state))
  // (D12 live updates) Someone else changed it: re-read quietly — unless the
  // pencil is open, then the draft stays and the card says so (Reload).
  const quietLoad = useCallback(() => {
    getLitigationFriendApi(caseId).then((s) => { setState(s); setLoadErr(null) }).catch(() => { /* keep what's shown */ })
  }, [caseId])
  const live = useLiveEditNotice(caseId, LF_LIVE_KINDS, editing)
  useCaseChange(caseId, LF_LIVE_KINDS, (_k, info) => { if (info.remote && draft === null) quietLoad() })
  const consentDirty = consentOpen && (cMethod !== '' || norm(cEmail) !== norm(state?.litigationFriendEmail))

  const open = (): void => {
    if (!state) return
    const d = pick(state)
    setDraft(d); setServerErr({}); setSaveErr(null); setTried(false); setOrderTyping(false); setViewErr(null); setLfSeparate(false)
    setSameAsClient(clientHas && anyAddress(lfAddressOf(d)) && sameAddress(lfAddressOf(d), clientAddress))
  }
  const cancel = (): void => { setDraft(null); setServerErr({}); setSaveErr(null); setTried(false) }
  const set = <K extends keyof Representatives>(k: K, v: Representatives[K]): void => {
    setDraft((x) => (x ? { ...x, [k]: v } : x))
    setServerErr((e) => { if (!(k in e)) return e; const n = { ...e }; delete n[k]; return n })
    setSaveErr(null)
  }
  // The five address fields at once (the lookup / the tick fill them all).
  const setAddress = (a: AddressValue): void => {
    setDraft((x) => (x ? { ...x, ...addressFields(a) } : x))
    setServerErr((e) => { if (!ADDR_FIELD_KEYS.some((k) => k in e)) return e; const n = { ...e }; for (const k of ADDR_FIELD_KEYS) delete n[k]; return n })
    setSaveErr(null)
  }
  const tickSame = (on: boolean): void => {
    setSameAsClient(on)
    if (on && clientAddress) setAddress({ ...EMPTY_ADDRESS, ...clientAddress })
  }
  // (E-sign fixes B) The draft's codes mode: MAIN (the phone is a UK mobile) hides the separate field.
  const draftCodes = draft ? codesMobileView({ phone: draft.litigationFriendPhone, mobile: draft.litigationFriendMobile }) : null
  const draftSeparate = !!draftCodes && (draftCodes.mode === 'SEPARATE' || lfSeparate)
  const local = draft ? {
    litigationFriendEmail: emailFormatError(draft.litigationFriendEmail), litigationFriendPhone: phoneFormatError(draft.litigationFriendPhone),
    litigationFriendMobile: pilot && draftSeparate ? mobileInputError(draft.litigationFriendMobile ?? '') : null,
  } : {}
  const fieldErr = (k: string): string | undefined => serverErr[k] ?? (tried ? (local as Record<string, string | null>)[k] ?? undefined : undefined)
  const save = async (): Promise<void> => {
    if (!draft || busy) return
    setTried(true); setSaveErr(null)
    if (Object.values(local).some(Boolean) || orderTyping) return
    setBusy(true)
    try {
      // The address fields go only when the address changed — so an old
      // address that couldn't be split (shown as typed) isn't wiped by saving
      // another field. The old one-box text is never sent (the server keeps it).
      const { litigationFriendAddress: _old, ...rest } = draft
      const addrChanged = !state || JSON.stringify(lfAddressOf(draft)) !== JSON.stringify(lfAddressOf(state))
      // B4c (D4) The mobile goes only when it changed (and the pilot is on).
      // (E-sign fixes B) MAIN → null (the server uses the phone); SEPARATE → as typed
      // (the same number as the phone counts as MAIN).
      const phoneE164 = normaliseUkMobile(draft.litigationFriendPhone)
      const typedE164 = draftSeparate ? mobileE164Of(draft.litigationFriendMobile ?? '') : null
      const sendMobile = !draftSeparate || !typedE164 || typedE164 === phoneE164 ? null : (draft.litigationFriendMobile ?? '').trim()
      const storedE164 = state?.litigationFriendMobile ? (normaliseUkMobile(state.litigationFriendMobile) ?? state.litigationFriendMobile) : null
      const mobileChanged = pilot && (sendMobile === null ? null : typedE164) !== storedE164
      const body: Partial<Representatives> = Object.fromEntries(Object.entries(rest).filter(([k]) =>
        (addrChanged || !ADDR_FIELD_KEYS.includes(k)) && k !== 'litigationFriendMobile'))
      if (mobileChanged) body.litigationFriendMobile = sendMobile
      const s = await putLitigationFriendApi(caseId, body)
      setState(s); setDraft(null); setTried(false); setServerErr({})
      emitCaseChange(caseId, 'litigationFriend')
    } catch (e) {
      const fields = serverFieldErrors(e)
      setServerErr(fields)
      if (!Object.keys(fields).length) setSaveErr(errorText(e, 'Couldn’t save the litigation friend.'))
    } finally { setBusy(false) }
  }
  const startConsent = (): void => {
    setCEmail((state?.litigationFriendEmail ?? '').trim()); setCDate(londonToday()); setCMethod(''); setCErr(null); setViewErr(null); setConsentOpen(true)
  }
  // (Audit fix batch 2) Cancel → back to the notice, focus on its "Record consent".
  const cancelConsent = (): void => {
    setConsentOpen(false); setCErr(null)
    window.setTimeout(() => document.getElementById(`${id}-crecord`)?.focus(), 0)
  }
  const recordConsent = async (): Promise<void> => {
    setCErr(null)
    if (!isValidEmail(cEmail)) { setCErr({ field: 'email', text: EMAIL_FORMAT_ERROR }); return }
    if (!cDate) { setCErr({ field: 'date', text: 'Enter the date they agreed.' }); return }
    if (!cMethod) { setCErr({ field: 'method', text: 'Choose how they agreed.' }); return }
    setBusy(true)
    try {
      const s = await recordLitigationFriendConsentApi(caseId, { date: cDate, email: cEmail.trim(), method: cMethod })
      setState(s); setConsentOpen(false)
      emitCaseChange(caseId, 'litigationFriend')
    } catch (e) {
      const fields = serverFieldErrors(e)
      const k = Object.keys(fields)[0]
      setCErr(k ? { field: k, text: fields[k] } : { text: errorText(e, 'Couldn’t record the consent.') })
    } finally { setBusy(false) }
  }
  const removeConsent = async (): Promise<void> => {
    setBusy(true); setViewErr(null)
    try {
      setState(await removeLitigationFriendConsentApi(caseId))
      emitCaseChange(caseId, 'litigationFriend')
    } catch (e) { setViewErr(errorText(e, 'Couldn’t remove the consent.')) } finally { setBusy(false) }
  }

  const errProps = (k: string) => ({ 'aria-invalid': !!fieldErr(k) || undefined, 'aria-describedby': fieldErr(k) ? `${id}-${k}-err` : undefined })
  const fieldErrEl = (k: string): React.ReactNode => <span id={`${id}-${k}-err`}><FieldError message={fieldErr(k)} /></span>
  const field = (k: keyof Representatives, label: string, extra: React.InputHTMLAttributes<HTMLInputElement> = {}, note?: React.ReactNode): React.ReactNode => (
    <FormField label={label} variant="info" htmlFor={`${id}-${k}`}>
      <input id={`${id}-${k}`} className={INPUT} value={((draft?.[k]) as string) ?? ''} onChange={(e) => set(k, e.target.value as never)} {...errProps(k)} {...extra} />
      {fieldErrEl(k)}
      {note}
    </FormField>
  )
  const landlineNote = <p className="m-0 mt-1 text-[12px] text-casevo-warn" data-testid="lf-landline-note">{LANDLINE_NOTE}.</p>
  const PHONE_CODES_LABEL = `Phone ${CODES_LABEL_SUFFIX}`
  const c = state?.emailConsent ?? null
  const savedEmail = state?.litigationFriendEmail ?? ''
  const changed = !!c && !!savedEmail.trim() && norm(c.email) !== norm(savedEmail)
  const who = childOrProtected === 'CHILD' ? 'child' : 'protected party'
  const lfName = state?.litigationFriendName.trim() ?? ''

  const row = (label: string, value: React.ReactNode, empty = false): React.ReactNode => (
    <>
      <dt className="text-casevo-text-soft">{label}</dt>
      <dd className={`m-0 min-w-0 break-words ${empty ? 'text-casevo-text-soft' : 'text-casevo-forest'}`}>{value}</dd>
    </>
  )
  const val = (v: string | null | undefined): [React.ReactNode, boolean] => ((v ?? '').trim() ? [v as string, false] : ['Not recorded', true])
  const fAddr = lfAddressOf(f)
  const addrSame = clientHas && anyAddress(fAddr) && sameAddress(fAddr, clientAddress)
  // The old one-box text that couldn't be split: shown as typed until it's re-entered.
  const legacyText = state?.litigationFriendAddressLegacy && !anyAddress(lfAddressOf(state)) ? state.litigationFriendAddress.trim() : ''
  const legacyNote = (
    <p className="m-0 mt-1 text-[12.5px] text-casevo-text-soft">{LEGACY_ADDRESS_NOTE}</p>
  )
  const addrLines = addressLinesOf(fAddr)
  // (E-sign fixes B) The read view's codes mode.
  const viewCodes = f ? codesMobileView({ phone: f.litigationFriendPhone, mobile: f.litigationFriendMobile }) : null
  const pc = fAddr.postcode.trim()

  const view = f && (
    <div className="grid gap-[14px]">
      <dl className="m-0 grid grid-cols-[minmax(110px,170px)_1fr] gap-x-3 gap-y-[6px] text-[13.5px]">
        {row('Name', ...val(f.litigationFriendName))}
        {row('Relationship', ...val(f.litigationFriendRelationship))}
        {legacyText
          ? row('Address', <><span className="whitespace-pre-line">{legacyText}</span>{legacyNote}</>)
          : row('Address', addrLines.length
            ? <span className="whitespace-pre-line">{addrLines.join('\n')}{addrSame && <span className="text-casevo-text-soft"> (same as the client)</span>}</span>
            : 'Not recorded', !addrLines.length)}
        {!legacyText && row('Postcode', pc ? <span className="font-mono text-[13px] tracking-[0.04em]">{pc}</span> : 'Not recorded', !pc)}
        {row('Email', ...val(f.litigationFriendEmail))}
        {pilot && viewCodes?.mode === 'MAIN'
          ? row(PHONE_CODES_LABEL, <><span className="tabular-nums" data-testid="lf-phone-codes">{f.litigationFriendPhone}</span>
            <MobileChangedHint load={() => getLitigationFriendMobileChangesApi(caseId)} refreshKey={`${caseId}:${state?.litigationFriendMobile ?? ''}`} /></>)
          : row('Phone', (f.litigationFriendPhone ?? '').trim() ? <>{f.litigationFriendPhone}{pilot && viewCodes?.landlineNote && landlineNote}</> : 'Not recorded', !(f.litigationFriendPhone ?? '').trim())}
        {pilot && viewCodes?.mode === 'SEPARATE' && row(MOBILE_LABEL, <>
          {f.litigationFriendMobile ? <span className="tabular-nums" data-testid="lf-mobile-value">{f.litigationFriendMobile}</span> : 'Not recorded'}
          <MobileChangedHint load={() => getLitigationFriendMobileChangesApi(caseId)} refreshKey={`${caseId}:${state?.litigationFriendMobile ?? ''}`} />
        </>, !f.litigationFriendMobile)}
      </dl>
      <div>
        <h4 className="m-0 text-[13.5px] font-semibold text-casevo-forest">Deputy or attorney</h4>
        <dl className="m-0 mt-[6px] grid grid-cols-[minmax(110px,170px)_1fr] gap-x-3 gap-y-[6px] text-[13.5px]">
          {row('Type', f.representativeType ? REPRESENTATIVE_TYPE_LABELS[f.representativeType] : 'None recorded', !f.representativeType)}
          {f.representativeType && <>
            {row('Name', ...val(f.representativeName))}
            {row('Reference', ...val(f.representativeReference))}
            {row('Order date', f.representativeOrderDate ? fmtDate(f.representativeOrderDate, 'long') : 'Not recorded', !f.representativeOrderDate)}
          </>}
        </dl>
      </div>
    </div>
  )

  const edit = draft && (
    <div className="flex flex-col gap-[14px]">
      <div className="grid gap-[12px] sm:grid-cols-2">
        {field('litigationFriendName', 'Name', { maxLength: 200 })}
        {field('litigationFriendRelationship', 'Relationship to the claimant', { maxLength: 100, placeholder: 'e.g. Mother' })}
        <div className="sm:col-span-2 grid gap-[10px]">
          <label className="inline-flex items-center gap-2 text-[13.5px] text-casevo-forest w-fit">
            <input type="checkbox" checked={sameAsClient} disabled={!clientHas && !sameAsClient} onChange={(e) => tickSame(e.target.checked)} />
            Same address as the client
          </label>
          {!clientHas && !sameAsClient && <p className={`${NOTE} m-0 -mt-[6px]`}>The client has no address on this case yet.</p>}
          {/* The client's address block: postcode search + the five fields. */}
          <div>
            <AddressFieldsEdit idPrefix={`${id}-lf`} value={lfAddressOf(draft)} onChange={setAddress} locked={sameAsClient}
              errorOf={(k) => fieldErr(ADDR_KEYS[k])}
              note={legacyText && !anyAddress(lfAddressOf(draft)) ? (
                <div className="mb-3 rounded-[9px] border border-casevo-border-soft bg-casevo-input px-3 py-2 text-[13px]">
                  <div className="text-casevo-text-soft text-[12px]">Saved as one block of text:</div>
                  <div className="whitespace-pre-line text-casevo-forest">{legacyText}</div>
                  {legacyNote}
                </div>
              ) : null} />
          </div>
        </div>
        {field('litigationFriendEmail', 'Email', { type: 'email', maxLength: 200 })}
        {field('litigationFriendPhone', pilot && !draftSeparate ? PHONE_CODES_LABEL : 'Phone', { type: 'tel', maxLength: 40 }, pilot && draftCodes?.landlineNote ? landlineNote : null)}
        {pilot && draftSeparate && <MobileInput id={`${id}-litigationFriendMobile`} value={draft.litigationFriendMobile ?? ''} className={INPUT}
          onChange={(v) => set('litigationFriendMobile', v)} error={fieldErr('litigationFriendMobile')}
          hint={normaliseUkMobile(draft.litigationFriendPhone) ? `${MOBILE_HINT} Leave blank to use the phone number.` : MOBILE_HINT} />}
        {pilot && !draftSeparate && (
          <div className="self-end">
            <button type="button" className="text-[12.5px] font-semibold text-casevo-brand dark:text-casevo-mid hover:underline underline-offset-2 focus:outline-none casevo-focus rounded-[3px]"
              onClick={() => { setLfSeparate(true); set('litigationFriendMobile', '') }}>Use a different mobile for codes</button>
          </div>
        )}
      </div>
      <div>
        <h4 className="m-0 text-[13.5px] font-semibold text-casevo-forest">Deputy or attorney</h4>
        <p className={`${NOTE} m-0`}>Who manages the money — a Court of Protection deputy or an attorney under a lasting power of attorney. Either covers the £100,000 Court of Protection check.</p>
      </div>
      <div className="grid gap-[12px] sm:grid-cols-2">
        <FormField label="Type" variant="info" htmlFor={`${id}-representativeType`}>
          <select id={`${id}-representativeType`} className={INPUT} value={draft.representativeType ?? ''} onChange={(e) => set('representativeType', (e.target.value || null) as RepresentativeType | null)} {...errProps('representativeType')}>
            <option value="">None recorded</option>
            {(Object.keys(REPRESENTATIVE_TYPE_LABELS) as RepresentativeType[]).map((t) => <option key={t} value={t}>{REPRESENTATIVE_TYPE_LABELS[t]}</option>)}
          </select>
          {fieldErrEl('representativeType')}
        </FormField>
        {field('representativeName', 'Deputy / attorney name', { maxLength: 200 })}
        {field('representativeReference', 'Reference (order or OPG number)', { maxLength: 200 })}
        <FormField label="Order date" variant="info">
          <DateInput value={draft.representativeOrderDate} onChange={(v) => set('representativeOrderDate', v)} ariaLabel="Order date" allowFuture={false} onValidityChange={(ok) => setOrderTyping(!ok)} />
          {fieldErrEl('representativeOrderDate')}
        </FormField>
      </div>
    </div>
  )

  const consent = state && !editing && (
    <div aria-label="Litigation friend email consent" role="group" className="mt-[14px] border-t border-casevo-border-soft pt-[12px]">
      <h4 className="m-0 text-[13.5px] font-semibold text-casevo-forest">Bills and statements by email</h4>
      {!lfName
        ? <p className={`${NOTE} m-0 mt-1`}>Add the litigation friend first (the pencil above).</p>
        : !consentOpen && (
          <div className="mt-2 grid gap-2">
            {c && <p className="m-0 text-[13.5px]">Agreed to receive them at <b className="break-all">{c.email}</b> on {fmtDate(c.at, 'long')} ({methodLabel(c.method)}){c.recordedBy ? ` — recorded by ${c.recordedBy}` : ''}.</p>}
            {!c && (
              <ActionBox ariaLabel="Litigation friend email consent needed" title="Consent needed to email bills and statements"
                message={`${state.litigationFriendName} hasn’t agreed to receive bills or statements by email. Until they do, send them by post or by hand.`}
                actions={[{ label: 'Record consent', kind: 'primary', onClick: startConsent, disabled: busy, id: `${id}-crecord` }]} />
            )}
            {changed && c && (
              <ActionBox ariaLabel="Litigation friend email changed" title="The litigation friend’s email has changed"
                message={consentChangedMessage(c.email ?? '', savedEmail.trim())}
                actions={[
                  { label: 'Remove consent', kind: 'link', onClick: () => { void removeConsent() }, busy, busyLabel: 'Removing…' },
                  { label: 'Record new consent', kind: 'primary', onClick: startConsent, disabled: busy, id: `${id}-crecord` },
                ]} />
            )}
            {c && !changed && (
              <div className="flex flex-wrap gap-2">
                <button type="button" className={B_SECONDARY_SM} disabled={busy} onClick={startConsent}><BIcon name="plus" size={12} />Record new consent</button>
                <button type="button" className={B_SECONDARY_SM} disabled={busy} onClick={() => { void removeConsent() }}>{busy ? 'Removing…' : 'Remove consent'}</button>
              </div>
            )}
          </div>
        )}
      {viewErr && <p role="alert" className="m-0 mt-2 text-[13px] text-casevo-danger">{viewErr}</p>}
      {consentOpen && (
        <div className="mt-2 flex flex-col gap-[10px]">
        <ActionBox tone="warn" variant="slim" title={changed ? 'The litigation friend’s email has changed' : 'Consent needed to email bills and statements'} status="· recording…" />
        <ConsentEditCard context={<>{state?.litigationFriendName} · bills and statements by email</>} focusId={`${id}-cemail`} busy={busy}
          onSave={() => { void recordConsent() }} onCancel={cancelConsent} footNote="Kept with the litigation friend’s details."
          error={cErr && !['email', 'date', 'method'].includes(cErr.field ?? '') ? cErr.text : null}>
          <FormField label="Exact email address agreed" required variant="info" htmlFor={`${id}-cemail`}>
            <input id={`${id}-cemail`} type="email" className={INPUT} value={cEmail} maxLength={200} onChange={(e) => setCEmail(e.target.value)}
              aria-invalid={cErr?.field === 'email' || undefined} aria-describedby={cErr?.field === 'email' ? `${id}-cemail-err` : undefined} />
            <span id={`${id}-cemail-err`}><FieldError message={cErr?.field === 'email' ? cErr.text : undefined} /></span>
          </FormField>
          <FormField label="Date agreed" required variant="info">
            <DateInput value={cDate} onChange={setCDate} ariaLabel="Date agreed" allowFuture={false}
              describedBy={cErr?.field === 'date' ? `${id}-cdate-err` : undefined} invalid={cErr?.field === 'date'} />
            <span id={`${id}-cdate-err`}><FieldError message={cErr?.field === 'date' ? cErr.text : undefined} /></span>
          </FormField>
          <FormField label="How they agreed" required variant="info" htmlFor={`${id}-cmethod`}>
            <select id={`${id}-cmethod`} className={INPUT} value={cMethod} onChange={(e) => setCMethod(e.target.value)}
              aria-invalid={cErr?.field === 'method' || undefined} aria-describedby={cErr?.field === 'method' ? `${id}-cmethod-err` : undefined}>
              <option value="">Choose…</option>
              {CONSENT_METHOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <span id={`${id}-cmethod-err`}><FieldError message={cErr?.field === 'method' ? cErr.text : undefined} /></span>
          </FormField>
        </ConsentEditCard>
        </div>
      )}
    </div>
  )

  const blocked = tried && (Object.values(local).some(Boolean) || orderTyping)
  return (
    <section id={LF_ROOT_ID} ref={rootRef} aria-label="Litigation friend" tabIndex={-1}
      className={`${CARD} mt-4 overflow-hidden scroll-mt-4 focus:outline-none transition-[border-color,box-shadow] ${editing ? CARD_EDITING : ''}`}>
      <UnsavedGuard dirty={dirty || consentDirty} />
      <CardHead icon="user" title="Litigation friend" sub={`Who acts for the ${who}. Bills, credit notes and the settlement statement are sent to the litigation friend.`}
        right={state && (editing
          ? <span className="text-[12.5px] text-casevo-text-soft">Editing</span>
          : <button type="button" id={LF_EDIT_ID} className={PENCIL} onClick={open} disabled={consentOpen} title="Edit litigation friend" aria-label="Edit litigation friend">{IcEdit(15)}</button>)} />
      <div className="px-5 pb-[18px]">
        {loading && !state && <p role="status" className={`${NOTE} m-0`}>Loading the litigation friend…</p>}
        {loadErr && !loading && (
          <div role="alert" className="flex items-center gap-3 flex-wrap text-[13px] text-casevo-danger">
            <span>{loadErr}</span>
            <button type="button" data-lf-retry="" className={B_SECONDARY_SM} onClick={() => { load() }}>Retry</button>
          </div>
        )}
        {editing && <LiveUpdateNote notice={live.notice} dirty={dirty} className="mb-3"
          onReload={() => { setDraft(null); setServerErr({}); setSaveErr(null); setTried(false); live.clear(); quietLoad() }} />}
        {editing ? edit : view}
        {consent}
      </div>
      {editing && (
        <div className="flex items-center gap-2 justify-end flex-wrap py-[10px] px-[14px] bg-casevo-input border-t border-casevo-border-soft">
          <span className="mr-auto text-[12px] text-casevo-text-soft">Nothing saves until you press Save · Cancel discards</span>
          {blocked && <span role="alert" className="text-[12px] text-casevo-danger">Fix the highlighted fields to save.</span>}
          {saveErr && <span role="alert" className="text-[12px] text-casevo-danger">{saveErr}</span>}
          <button type="button" className={B_SECONDARY_SM} onClick={cancel} disabled={busy}>Cancel</button>
          <button type="button" className={B_PRIMARY.replace('h-9', 'h-[30px]')} onClick={() => { void save() }} disabled={busy || !dirty}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      )}
    </section>
  )
}
export default LitigationFriendCard
